import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ReactFlow,
  Controls,
  Background,
  addEdge,
  Connection,
  Edge,
  Node,
  OnConnect,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  getBezierPath,
  EdgeProps,
  BaseEdge,
  EdgeLabelRenderer
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Save,
  Webhook, X, Bot,
  Zap, Mail, Target, Loader2,
  Layout, Snowflake, Flame, Trash2, Edit3, Library, Plus,
  Activity, Filter, Clock, BarChart2, Play, Share2, ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { AutomationNode } from '../components/AutomationNode';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Automation, LogEntry } from '../lib/local-ai/types';
import { automationTemplates, AutomationTemplate } from '../data/automationTemplates';
import { useStore } from '../store/useStore';
import { createMultiStepWorkflow } from '../lib/workflow-builder';
import { Modal } from '../components/Modal';

const nodeTypes = {
  automation: AutomationNode,
};

function DeleteableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps) {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleEnter = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setIsHovered(true);
  };

  const handleLeave = () => {
    leaveTimer.current = setTimeout(() => setIsHovered(false), 80);
  };

  const onEdgeClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setEdges((edges: Edge[]) => edges.filter((edge: Edge) => edge.id !== id));
    toast.success('Connection removed');
  };

  return (
    <g
      style={{ cursor: 'pointer' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
      />
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={{ ...style, cursor: 'pointer' }} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 10,
            pointerEvents: isHovered ? 'all' : 'none',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.15s ease-in-out',
          }}
          className="nodrag nopan"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <button
            style={{
              backgroundColor: '#ef4444',
              boxShadow: '0 0 10px rgba(239,68,68,0.5)',
              width: 20,
              height: 20,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(255,255,255,0.3)',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={onEdgeClick}
            title="Delete connection"
          >
            <X size={10} strokeWidth={4} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </g>
  );
}

const edgeTypes = {
  deletable: DeleteableEdge,
};

const debugStyles = `
  .react-flow__node {
    z-index: 1000 !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
`;

interface AutomationNodeData extends Record<string, unknown> {
  label?: string;
  type?: string;
  description?: string;
  triggerType?: string;
  actionType?: string;
  status?: string;
  threshold?: number;
  message?: string;
  subject?: string;
  duration?: number;
  prompt?: string;
  model?: string;
  // Condition fields
  conditionField?: string;
  conditionOperator?: string;
  conditionValue?: string;
  // Task fields  
  taskTitle?: string;
  priority?: string;
  // Delay fields
  delayUnit?: string;
  webhookUrl?: string;
  slackChannel?: string;
  assignedTo?: string;
  tagName?: string;
}

const initialNodes: Node[] = [
  {
    id: '1',
    type: 'automation',
    position: { x: 250, y: 150 },
    data: {
      label: 'New Trigger',
      type: 'trigger',
      description: 'Start your automation here.'
    },
  }
];

const initialEdges: Edge[] = [];

function AutomationsHubContent() {
  // State for Canvas using XYFlow hooks (v12 best practice)
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [workflowName, setWorkflowName] = useState('My New Automation');
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ id: string, name: string } | null>(null);
  const [showCreateCustomModal, setShowCreateCustomModal] = useState(false);
  const [newCustomNode, setNewCustomNode] = useState({
    label: '',
    type: 'action' as 'trigger' | 'action' | 'condition' | 'delay',
    actionType: 'send_sms',
    triggerType: 'new_lead',
    conditionField: 'status',
    conditionOperator: 'equals',
    conditionValue: '',
    description: ''
  });
  const {
    isAutomationRunning, currentUser, customNodes, addCustomNode, deleteCustomNode,
    myAutomations, setMyAutomations, myBlueprints, fetchMyAutomations, fetchMyBlueprints, 
    saveAsBlueprint, shareBlueprint, deleteBlueprint, fetchRecentLogs, recentLogs
  } = useStore();
  const connectingNodeId = useRef<string | null>(null);

  const [logStatusFilter, setLogStatusFilter] = useState<'all' | 'success' | 'failure'>('all');
  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');

  // Scroll lock when modals are open
  useEffect(() => {
    if (showCreateCustomModal || isSettingsOpen || deleteConfirmation) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showCreateCustomModal, isSettingsOpen, deleteConfirmation]);

  const [activeTemplateTab, setActiveTemplateTab] = useState<'featured' | 'mine'>('featured');

  const onInit = useCallback((_instance: any) => {
    // Canvas initialized
  }, []);

  const saveAsCustomNode = (node: Node) => {
    const newNode = {
      ...node,
      data: {
        ...node.data,
        label: `${(node.data as any).label || 'Custom'} (Saved)`
      }
    };
    addCustomNode(newNode);
    toast.success('Node saved to Custom Registry');
  };

  const [viewMode, setViewMode] = useState<'editor' | 'dashboard'>('dashboard');
  const [, setPrefs] = useState<any>(null);

  const { fitView, screenToFlowPosition } = useReactFlow();

  // Load Preferences & Data
  useEffect(() => {
    if (currentUser?.id) {
      fetchPreferences();
      fetchMyAutomations();
      fetchMyBlueprints();
      fetchRecentLogs();
    }
  }, [currentUser?.id, fetchMyAutomations, fetchMyBlueprints, fetchRecentLogs]);

  // Debug nodes state and DOM
  useEffect(() => {
    console.log('[AutomationsHub] Nodes state updated:', nodes.length, 'nodes total');
    const nodeElements = document.querySelectorAll('.react-flow__node');
    console.log(`[AutomationsHub] DOM Check: Found ${nodeElements.length} node elements in DOM`);
    if (nodeElements.length > 0) {
      nodeElements.forEach((el, i) => {
        const style = window.getComputedStyle(el);
        console.log(`Node ${i} Style: position=${style.position}, transform=${style.transform}, opacity=${style.opacity}, visibility=${style.visibility}`);
      });
    }
  }, [nodes]);

  // Use store fetchers

  const testWorkflow = async (automation: Automation) => {
    try {
      const nodes = automation.nodes as Node[];
      const triggerNode = nodes.find(n => n.data?.type === 'trigger');
      
      if (!triggerNode) {
        toast.error("No trigger node found in this workflow.");
        return;
      }

      const triggerType = triggerNode.data?.triggerType;
      console.log(`[AutomationsHub] Testing workflow "${automation.name}" with trigger: ${triggerType}`);

      let eventType: any = 'LEAD_CREATED';
      let dummyData: any = { id: 'test-lead-id', name: 'Test User', email: 'test@example.com', deal_score: 85 };

      // Map trigger node type to engine event type
      if (triggerType === 'status_change') {
        eventType = 'LEAD_STATUS_CHANGED';
        dummyData.status = triggerNode.data?.status || 'closed-won';
      } else if (triggerType === 'task_overdue') {
        eventType = 'TASK_STATUS_CHANGED';
        dummyData.status = 'overdue';
      } else if (triggerType === 'high_score') {
        eventType = 'LEAD_SCORE_HIGH';
        dummyData.deal_score = triggerNode.data?.threshold || 90;
      } else if (triggerType === 'email_opened') {
        eventType = 'EMAIL_OPENED';
      } else if (triggerType === 'sms_received') {
        eventType = 'SMS_RECEIVED';
      } else if (triggerType === 'new_event') {
        eventType = 'CALENDAR_EVENT_CREATED';
      }

      const { automationEngine } = await import('../lib/automation-engine');
      toast.loading(`Simulating ${eventType}...`, { id: 'test-run' });
      await automationEngine.trigger(eventType, { ...dummyData, user_id: currentUser?.id });
      toast.success("Test run complete. Check console for logs.", { id: 'test-run' });
      
      // Refresh logs after a short delay
      setTimeout(fetchRecentLogs, 3000);
    } catch (err) {
      console.error('Test run failed:', err);
      toast.error("Test run failed. See console.", { id: 'test-run' });
    }
  };

  const fetchPreferences = async () => {
    if (!isSupabaseConfigured || !supabase) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_os_messages_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (data) setPrefs(data);
    } catch (err) {
      console.error('Fetch preferences error:', err);
    }
  };

  const toggleAutomationActive = async (id: string, currentStatus: boolean) => {
    if (!isSupabaseConfigured || !supabase) return;
    
    const nextStatus = !currentStatus;
    
    // Optimistic Update
    setMyAutomations((prev: Automation[]) => prev.map((a: Automation) => a.id === id ? { ...a, is_active: nextStatus } : a));
    
    try {
      const { error } = await supabase
        .from('user_automations')
        .update({ is_active: nextStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        // Rollback
        setMyAutomations((prev: any[]) => prev.map((a: any) => a.id === id ? { ...a, is_active: currentStatus } : a));
        throw error;
      }
      
      toast.success(nextStatus ? 'Automation Resumed' : 'Automation Paused', {
        style: { background: 'var(--t-surface)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }
      });
    } catch (err) {
      toast.error('Failed to toggle status');
      // Rollback already handled in error block if it came from supabase
    }
  };

  const deleteWorkflow = async () => {
    if (!isSupabaseConfigured || !supabase || !deleteConfirmation) return;
    const { id, name } = deleteConfirmation;

    try {
      const { error } = await supabase
        .from('user_automations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success(`Deleted workflow: ${name}`);
      setMyAutomations((prev: Automation[]) => prev.filter((a: Automation) => a.id !== id));
      if (workflowId === id) {
        setWorkflowId(null);
        setWorkflowName('My New Automation');
        setNodes(initialNodes);
        setEdges([]);
        setViewMode('dashboard');
      }
    } catch (err) {
      toast.error('Failed to delete workflow');
    } finally {
      setDeleteConfirmation(null);
    }
  };

  const editWorkflow = (automation: Automation) => {
    setWorkflowId(automation.id);
    setWorkflowName(automation.name);
    setNodes(automation.nodes || initialNodes);
    // Map edges to 'deletable' type for hover logic
    const edgesWithTypes = (automation.edges || []).map((edge: any) => ({
      ...edge,
      type: 'deletable',
      animated: true,
      style: { stroke: 'var(--t-primary)' }
    }));
    setEdges(edgesWithTypes);
    setIsActive(automation.is_active);
    setViewMode('editor');
    setTimeout(() => fitView({ duration: 800, padding: 0.2 }), 100);
  };

  const saveWorkflow = async () => {
    if (!isSupabaseConfigured || !supabase) return;

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user');

      const workflowData = {
        user_id: user.id,
        name: workflowName,
        nodes,
        edges,
        is_active: isActive,
        updated_at: new Date().toISOString(),
      };

      if (workflowId) {
        await supabase.from('user_automations').update(workflowData).eq('id', workflowId);
      } else {
        const { data } = await supabase.from('user_automations').insert([workflowData]).select().single();
        if (data) setWorkflowId(data.id);
      }

      await fetchMyAutomations();
      toast.success('Automation saved successfully!');
    } catch (err) {
      toast.error('Failed to save automation');
    } finally {
      setIsSaving(false);
    }
  };

  const saveAsBlueprintUI = async () => {
    setIsSaving(true);
    try {
      const result = await saveAsBlueprint({
        name: workflowName,
        description: `Custom blueprint for ${workflowName}`,
        category: 'General',
        nodes,
        edges
      });
      if (result.success) {
        toast.success('Saved as reusable blueprint!');
        fetchMyBlueprints();
      } else {
        toast.error(result.error || 'Failed to save blueprint');
      }
    } catch (err) {
      toast.error('Error saving blueprint');
    } finally {
      setIsSaving(false);
    }
  };

  const importBlueprint = (blueprint: any) => {
    setWorkflowId(null);
    setWorkflowName(`${blueprint.name} (Copy)`);
    setNodes(blueprint.nodes || []);
    setEdges(blueprint.edges || []);
    setIsActive(true);
    setViewMode('editor');
    toast.success(`Imported blueprint: ${blueprint.name}`);
    setTimeout(() => fitView({ duration: 800, padding: 0.2 }), 100);
  };

  const importTemplate = async (template: AutomationTemplate) => {
    setWorkflowId(null);
    setWorkflowName(template.name);
    setNodes(JSON.parse(JSON.stringify(template.nodes)));
    // Ensure all imported edges use the deletable type for hover interactions
    const edgesWithTypes = (template.edges || []).map((edge: any) => ({
      ...edge,
      type: 'deletable',
      animated: true,
      style: { stroke: 'var(--t-primary)' }
    }));
    setEdges(edgesWithTypes);
    setIsActive(true);
    setViewMode('editor');
    toast.success(`Imported blueprint: ${template.name}`);
    setTimeout(() => fitView({ duration: 800, padding: 0.2 }), 100);
  };

  const createNewWorkflow = () => {
    const { nodes, edges } = createMultiStepWorkflow('New Smart Workflow');
    setWorkflowId(null);
    setWorkflowName('Smart Workflow');
    setNodes(nodes);
    // Ensure all generated edges use the deletable type
    const edgesWithTypes = edges.map((edge: any) => ({
      ...edge,
      type: 'deletable',
      animated: true,
      style: { stroke: 'var(--t-primary)' }
    }));
    setEdges(edgesWithTypes);
    setIsActive(true);
    setViewMode('editor');
    toast.success('Generated multi-step workflow!');
    setTimeout(() => fitView({ duration: 800, padding: 0.2 }), 100);
  };

  const onConnect: OnConnect = useCallback(
    (params: Connection) => setEdges((eds: Edge[]) => addEdge({ ...params, type: 'deletable', animated: true, style: { stroke: 'var(--t-primary)' } }, eds)),
    [setEdges],
  );

  const onNodeClick = useCallback((_: any, node: Node) => {
    setSelectedNode(node);
    setIsSettingsOpen(true);
  }, []);

  const onNodesDelete = useCallback((deletedNodes: Node[]) => {
    toast.success(`Deleted ${deletedNodes.length} node(s)`);
  }, []);

  const updateNodeData = (nodeId: string, newData: any) => {
    setNodes((nds: Node[]) => nds.map((n: Node) => n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n));
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev: Node | null) => prev ? { ...prev, data: { ...prev.data, ...newData } } : null);
    }
  };

  const addNode = (type: string) => {
    const id = `node-${Date.now()}`;
    const defaults: Record<string, Record<string, unknown>> = {
      trigger: { label: 'New Trigger', type: 'trigger', triggerType: 'new_lead', description: 'Starts the workflow' },
      action: { label: 'New Action', type: 'action', actionType: 'send_sms', description: 'Configure this step' },
      ai: { label: 'AI Engine', type: 'ai', model: 'gemini-2.0-flash-lite', description: 'AI processing step' },
      condition: { label: 'Condition', type: 'condition', conditionField: 'status', conditionOperator: 'equals', conditionValue: '', description: 'Branch logic' },
      delay: { label: 'Wait / Delay', type: 'delay', actionType: 'delay', duration: 3600, delayUnit: 'hours', description: 'Pause before next step' },
    };
    const newNode: Node = {
      id,
      type: 'automation',
      position: { x: 400, y: 200 },
      data: defaults[type] || { label: `New ${type.toUpperCase()}`, type, description: 'Configure this step' },
    };
    setNodes((nds: Node[]) => [...nds, newNode]);
    console.log('[AutomationsHub] Added node:', newNode);
    toast.success(`Added ${type} node`);
  };

  const onConnectStart = useCallback((_: any, { nodeId }: any) => {
    connectingNodeId.current = nodeId;
  }, []);

  const onConnectEnd = useCallback(
    (event: any) => {
      if (!connectingNodeId.current) return;

      const targetIsPane = event.target.classList.contains('react-flow__pane');

      if (targetIsPane) {
        // Robust coordinate handling for different event types
        let clientX = 0;
        let clientY = 0;

        if (event instanceof MouseEvent || (event.nativeEvent && event.nativeEvent instanceof MouseEvent)) {
          clientX = event.clientX;
          clientY = event.clientY;
        } else if (event.changedTouches && event.changedTouches.length > 0) {
          clientX = event.changedTouches[0].clientX;
          clientY = event.changedTouches[0].clientY;
        } else {
          // Fallback
          return;
        }

        const position = screenToFlowPosition({ x: clientX, y: clientY });

        const id = `node-${Date.now()}`;
        const newNode: Node = {
          id,
          type: 'automation',
          position,
          data: { label: 'New Action', type: 'action', actionType: 'send_sms', description: 'Configure this step' },
        };

        setNodes((nds: Node[]) => nds.concat(newNode));
        setEdges((eds: Edge[]) => eds.concat({
          id: `e-${connectingNodeId.current}-${id}`,
          source: connectingNodeId.current!,
          target: id,
          type: 'deletable',
          animated: true,
          style: { stroke: 'var(--t-primary)' }
        }));

        toast.success('Added connected action');
      }

      connectingNodeId.current = null;
    },
    [screenToFlowPosition]
  );

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    console.log('[AutomationsHub] Drag start:', nodeType);
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';

    // Create a ghost image to avoid the "blocked" icon
    // Using a simple transparent gif
    const ghost = new Image();
    ghost.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    event.dataTransfer.setDragImage(ghost, 0, 0);
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      console.log('[AutomationsHub] Drop event detected');

      let rawData = '';
      try {
        rawData = event.dataTransfer.getData('application/reactflow') ||
          event.dataTransfer.getData('text/plain') ||
          event.dataTransfer.getData('text');
      } catch (e) {
        console.warn('DnD data fetch failed', e);
      }

      if (!rawData) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (!position || isNaN(position.x) || isNaN(position.y)) return;

      const id = `node-${Date.now()}`;
      let nodeData: any = null;

      // Check if it's a JSON string (Custom Node)
      try {
        if (rawData.trim().startsWith('{')) {
          nodeData = JSON.parse(rawData);
        }
      } catch (e) {
        // Not JSON
      }

      const defaults: Record<string, Record<string, unknown>> = {
        trigger: { label: 'New Trigger', type: 'trigger', triggerType: 'new_lead', description: 'Starts the workflow' },
        action: { label: 'New Action', type: 'action', actionType: 'send_sms', description: 'Configure this step' },
        ai: { label: 'AI Engine', type: 'ai', model: 'gemini-1.5-pro', description: 'AI processing step' },
        condition: { label: 'Condition', type: 'condition', conditionField: 'status', conditionOperator: 'equals', conditionValue: '', description: 'Branch logic' },
        delay: { label: 'Wait / Delay', type: 'delay', actionType: 'delay', duration: 3600, delayUnit: 'hours', description: 'Pause before next step' },
      };

      const newNode: Node = {
        id,
        type: 'automation',
        position,
        data: nodeData || defaults[rawData] || { label: `New ${rawData.toUpperCase()}`, type: rawData, description: 'Configure this step' },
      };

      setNodes((nds: Node[]) => [...nds, newNode]);
      
      setTimeout(() => fitView({ duration: 400, padding: 0.2 }), 250);
      toast.success(`Added ${nodeData ? (nodeData.label || 'Custom') : rawData} node`);
    },
    [screenToFlowPosition, setNodes, fitView]
  );

  // Helper for rendering date
  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + new Date(date).toLocaleDateString();
  };



  return (
    <div className="min-h-screen pt-12 pb-20 px-6 max-w-6xl mx-auto space-y-10 animate-astral-hero">
      <style>{debugStyles}</style>

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-2 border-b border-[var(--t-border)]">
          <div className="pt-8 pb-4">
          <h1 className="text-4xl font-black italic uppercase tracking-tighter text-[var(--t-text)] flex items-center gap-3">
            <Zap className="text-[var(--t-primary)] fill-[var(--t-primary)] w-8 h-8" />
            Automations <span className="text-[var(--t-primary)]">Hub</span>
          </h1>
          <p className="text-[var(--t-text-muted)] text-sm font-medium mt-1 uppercase tracking-widest">Autonomous Workflow Engine v3.0</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (viewMode === 'dashboard') {
                setWorkflowId(null);
                setWorkflowName('My New Automation');
                setNodes(initialNodes);
                setEdges([]);
                setViewMode('editor');
              } else {
                setViewMode('dashboard');
              }
            }}
            className={`px-6 py-3 rounded-2xl font-bold flex items-center gap-2 transition-all border ${viewMode === 'editor'
                ? 'bg-red-500/10 border-red-500/20 text-red-500 hover:bg-red-500/20'
                : 'bg-[var(--t-primary)] text-white shadow-lg shadow-[var(--t-primary-dim)] hover:scale-105 active:scale-95'
              }`}
          >
            {viewMode === 'editor' ? <X size={20} /> : <Plus size={20} />}
            <span>{viewMode === 'editor' ? 'Close Editor' : 'Create Automation'}</span>
          </button>

          {viewMode === 'dashboard' && (
            <button
              onClick={createNewWorkflow}
              className="px-6 py-3 rounded-2xl font-bold flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 text-purple-500 hover:bg-purple-500/20 transition-all shadow-lg active:scale-95"
            >
              <Bot size={20} />
              <span>+ Workflow</span>
            </button>
          )}
        </div>
      </div>

      {viewMode === 'dashboard' && (
        <div
          key="dashboard"
          className="p-8 max-w-7xl mx-auto space-y-12"
        >
          {/* Section 1: Active Automations */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)] flex items-center gap-2">
                <Activity className="text-green-500" />
                Active Automations
              </h3>
              <div className="flex items-center gap-4">
                {isAutomationRunning && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)] text-white text-[10px] font-black uppercase tracking-widest rounded-full animate-pulse border border-green-400/30">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                    Engine Firing
                  </div>
                )}
                <div className="px-3 py-1 bg-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest border border-green-500/20 rounded-full font-bold">
                  {myAutomations.filter((a: Automation) => a.is_active).length} Running Now
                </div>
              </div>
            </div>

            {myAutomations.length === 0 ? (
              <div className="liquid-glass rounded-[2rem] p-12 flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[var(--t-surface-hover)] flex items-center justify-center text-[var(--t-text-muted)]">
                  <Snowflake size={32} />
                </div>
                <h4 className="text-lg font-bold text-[var(--t-text)]">No automations active</h4>
                <p className="text-[var(--t-text-muted)] max-w-sm">Bring your CRM to life by creating your first workflow or importing a template below.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myAutomations.map((automation: Automation) => (
                  <div
                    key={automation.id}
                    className="liquid-glass rounded-[2rem] p-6 hover:border-[var(--t-primary)] transition-all group relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--t-primary)] opacity-[0.03] blur-3xl -mr-16 -mt-16 group-hover:opacity-[0.08] transition-opacity" />

                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-all ${automation.is_active
                            ? 'bg-green-500/10 border-green-500/20 text-green-500'
                            : 'bg-white/5 border-white/10 text-[var(--t-text-muted)]'
                          }`}>
                          <Zap size={24} className={automation.is_active ? 'fill-current animate-pulse' : ''} />
                        </div>
                        <div>
                          <h4 className="font-black italic uppercase tracking-tighter text-[var(--t-text)] group-hover:text-[var(--t-primary)] transition-colors">{automation.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${automation.is_active ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]' : 'bg-[var(--t-text-muted)]'}`} />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--t-text-muted)]">
                              {automation.is_active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 relative z-20">
                        <button 
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            testWorkflow(automation);
                          }}
                          className="p-2.5 hover:bg-green-500/10 rounded-xl text-[var(--t-text-muted)] hover:text-green-500 transition-all"
                          title="Test Run"
                        >
                          <Play size={18} />
                        </button>
                        <button 
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            editWorkflow(automation);
                          }}
                          className="p-2.5 hover:bg-[var(--t-surface-hover)] rounded-xl text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-all"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button 
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            console.log('[AutomationsHub] Requesting delete for:', automation.id);
                            setDeleteConfirmation({ id: automation.id, name: automation.name });
                          }}
                          className="p-2.5 hover:bg-red-500/10 rounded-xl text-[var(--t-text-muted)] hover:text-red-500 transition-all"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 rounded-2xl bg-black/20 border border-white/5 flex flex-col gap-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Run Count</span>
                        <span className="text-xl font-black italic text-[var(--t-text)]">{automation.run_count || 0}</span>
                      </div>
                      <div className="p-4 rounded-2xl bg-black/20 border border-white/5 flex flex-col gap-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Success Rate</span>
                        <span className="text-xl font-black italic text-green-400">{automation.success_rate || 0}%</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-[var(--t-border)]">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Last Triggered</span>
                        <span className="text-[10px] font-bold text-[var(--t-text)]">{formatDate(automation.last_triggered_at ?? null)}</span>
                      </div>
                      <div 
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          toggleAutomationActive(automation.id, automation.is_active);
                        }}
                        className={`w-10 h-5 rounded-full p-0.5 cursor-pointer transition-all duration-300 relative ${
                          automation.is_active 
                            ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]' 
                            : 'bg-[var(--t-border)]'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 transform ${
                            automation.is_active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Section 2: Recent Activity */}
          {recentLogs.length > 0 && (
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)] flex items-center gap-2">
                  <BarChart2 className="text-blue-500" />
                  Recent Activity
                </h3>
                <div className="px-3 py-1 bg-blue-500/10 text-blue-500 text-[10px] font-black uppercase tracking-widest border border-blue-500/20 rounded-full">
                  {recentLogs.length} Entries
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4 bg-[var(--t-surface)] p-2 rounded-2xl border border-[var(--t-border)] w-fit">
                  <div className="flex items-center gap-2 px-3 border-r border-[var(--t-border)]">
                    <Filter size={14} className="text-[var(--t-text-muted)]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Filters</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <select 
                      value={logStatusFilter}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLogStatusFilter(e.target.value as any)}
                      className="bg-transparent text-xs font-bold text-[var(--t-text)] outline-none border-none focus:ring-0 cursor-pointer"
                    >
                      <option value="all">All Status</option>
                      <option value="success">Success Only</option>
                      <option value="failure">Failed Only</option>
                    </select>
                    <select 
                      value={logTypeFilter}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLogTypeFilter(e.target.value)}
                      className="bg-transparent text-xs font-bold text-[var(--t-text)] outline-none border-none focus:ring-0 cursor-pointer"
                    >
                      <option value="all">All Types</option>
                      {Array.from(new Set(recentLogs.map((l: LogEntry) => l.trigger_event).filter(Boolean))).map(type => (
                        <option key={type} value={type!}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="liquid-glass rounded-[2rem] overflow-hidden border border-[var(--t-border)]">
                  <div className="grid grid-cols-[1fr_120px_160px_100px] gap-4 px-6 py-3 border-b border-[var(--t-border)] text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] bg-white/5">
                    <span>Automation</span>
                    <span>Trigger</span>
                    <span>Executed</span>
                    <span>Status</span>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                    {recentLogs
                      .filter((log: LogEntry) => {
                        if (logStatusFilter !== 'all' && log.status !== logStatusFilter) return false;
                        if (logTypeFilter !== 'all' && log.trigger_event !== logTypeFilter) return false;
                        return true;
                      })
                      .map((log: LogEntry) => (
                        <div key={log.id} className="grid grid-cols-[1fr_120px_160px_100px] gap-4 px-6 py-4 border-b border-[var(--t-border)] last:border-b-0 hover:bg-[var(--t-surface-hover)] transition-colors items-center">
                          <span className="text-sm font-bold text-[var(--t-text)] truncate">{log.automation_name}</span>
                          <span className="text-xs text-[var(--t-text-muted)] truncate">{log.trigger_event || 'â€”'}</span>
                          <span className="text-xs text-[var(--t-text-muted)]">{formatDate(log.executed_at)}</span>
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full w-fit ${log.status === 'success'
                              ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                              : 'bg-red-500/10 text-red-500 border border-red-500/20'
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${log.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                            {log.status}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Section 3: Template Library */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)] flex items-center gap-2">
                <Library className="text-purple-500" />
                Template Library
              </h3>
              <div className="flex items-center gap-2 bg-[var(--t-surface)] p-1 rounded-xl border border-[var(--t-border)]">
                <button 
                  onClick={() => setActiveTemplateTab('featured')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    activeTemplateTab === 'featured' 
                      ? 'bg-[var(--t-surface-hover)] text-[var(--t-text)]' 
                      : 'text-[var(--t-text-muted)] hover:bg-[var(--t-surface-hover)]'
                  }`}
                >
                  Featured
                </button>
                <button 
                  onClick={() => setActiveTemplateTab('mine')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    activeTemplateTab === 'mine' 
                      ? 'bg-[var(--t-surface-hover)] text-[var(--t-text)]' 
                      : 'text-[var(--t-text-muted)] hover:bg-[var(--t-surface-hover)]'
                  }`}
                >
                  My Blueprints
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {activeTemplateTab === 'featured' ? (
                automationTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="liquid-glass rounded-2xl p-6 hover:shadow-2xl transition-all cursor-pointer group"
                    onClick={() => importTemplate(template)}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center text-[var(--t-primary)]">
                          {template.category === 'Lead Gen' && <Target size={20} />}
                          {template.category === 'AI' && <Bot size={20} />}
                          {template.category === 'CRM' && <Layout size={20} />}
                          {template.category === 'Sales' && <Flame size={20} />}
                          {template.category === 'Comms' && <Mail size={20} />}
                        </div>
                        <div className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">
                          {template.category}
                        </div>
                      </div>
                      <h4 className="font-black italic uppercase tracking-tighter text-[var(--t-text)] mb-2 group-hover:text-[var(--t-primary)] transition-colors">{template.name}</h4>
                      <p className="text-xs text-[var(--t-text-muted)] flex-1">{template.description}</p>
                      <div className="pt-4 mt-4 border-t border-[var(--t-border)] flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-primary)]">Import Library</span>
                        <ArrowRight size={14} className="text-[var(--t-primary)] opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                myBlueprints.map((blueprint: any) => (
                  <div
                    key={blueprint.id}
                    className="liquid-glass rounded-2xl p-6 hover:shadow-2xl transition-all group border border-purple-500/20"
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
                          <Zap size={20} />
                        </div>
                        <div className="flex items-center gap-2">
                           {blueprint.user_id === currentUser?.id && (
                             <button
                               onClick={() => shareBlueprint(blueprint.id, !blueprint.is_public)}
                               className={`p-1.5 rounded-lg border transition-all ${
                                 blueprint.is_public 
                                   ? 'bg-blue-500/20 border-blue-500/30 text-blue-400' 
                                   : 'bg-white/5 border-white/10 text-[var(--t-text-muted)] hover:bg-white/10'
                               }`}
                               title={blueprint.is_public ? 'Shared with team' : 'Private blueprint'}
                             >
                               <Share2 size={12} />
                             </button>
                           )}
                           <button
                             onClick={() => deleteBlueprint(blueprint.id)}
                             className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-red-400 hover:bg-red-500/20 hover:border-red-500/30 transition-all"
                           >
                             <Trash2 size={12} />
                           </button>
                        </div>
                      </div>
                      <div className="cursor-pointer" onClick={() => importBlueprint(blueprint)}>
                        <h4 className="font-black italic uppercase tracking-tighter text-[var(--t-text)] mb-2 group-hover:text-purple-400 transition-colors">{blueprint.name}</h4>
                        <p className="text-xs text-[var(--t-text-muted)] flex-1">{blueprint.description || 'Your custom saved workflow ready for reuse.'}</p>
                        <div className="pt-4 mt-4 border-t border-[var(--t-border)] flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest text-purple-400">Load Blueprint</span>
                          <ArrowRight size={14} className="text-purple-400 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {activeTemplateTab === 'mine' && myBlueprints.length === 0 && (
                <div className="col-span-full py-12 text-center liquid-glass rounded-2xl border border-dashed border-[var(--t-border)]">
                   <p className="text-[var(--t-text-muted)] font-bold uppercase tracking-widest text-sm">No saved blueprints yet</p>
                   <p className="text-[var(--t-text-muted)] text-[10px] mt-2">Save your first automation as a blueprint to see it here as a reusable template.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {viewMode === 'editor' && (
        <div
          key="editor"
          className="flex-1 h-[calc(100vh-64px)] flex flex-col relative overflow-hidden bg-[var(--t-bg)] z-[50]"
        >
          {/* Toolbar Panel */}
          <div className="absolute top-4 left-6 z-[100] flex items-center gap-4 bg-black/60 backdrop-blur-xl p-3 rounded-2xl border border-white/10 shadow-2xl">
            <div className="flex flex-col gap-0.5">
              <input
                type="text"
                value={workflowName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkflowName(e.target.value)}
                className="bg-transparent border-none text-white font-black italic uppercase tracking-tighter text-lg outline-none focus:ring-0 placeholder:opacity-30 w-48"
                placeholder="Automation Name"
              />
              <div className="flex items-center gap-2 text-[9px] font-bold text-white/40 uppercase tracking-widest pl-0.5">
                <span className="flex items-center gap-1"><Activity size={8} /> LIVE HUB EDITOR</span>
              </div>
            </div>

            <div className="w-px h-10 bg-white/10 mx-2" />

            <div className="flex items-center gap-2">
              <div
                draggable={true}
                onDragStart={(e: React.DragEvent) => onDragStart(e, 'trigger')}
                onClick={() => addNode('trigger')}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all flex items-center gap-2 font-black uppercase tracking-tighter text-[10px] cursor-grab active:cursor-grabbing"
              >
                <Webhook size={14} className="text-green-400" />
                + Trigger
              </div>
              <div
                draggable={true}
                onDragStart={(e: React.DragEvent) => onDragStart(e, 'condition')}
                onClick={() => addNode('condition')}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all flex items-center gap-2 font-black uppercase tracking-tighter text-[10px] cursor-grab active:cursor-grabbing"
              >
                <Filter size={14} className="text-orange-400" />
                + Condition
              </div>
              <div
                draggable={true}
                onDragStart={(e: React.DragEvent) => onDragStart(e, 'ai')}
                onClick={() => addNode('ai')}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all flex items-center gap-2 font-black uppercase tracking-tighter text-[10px] cursor-grab active:cursor-grabbing"
              >
                <Bot size={14} className="text-purple-400" />
                + AI Engine
              </div>
              <div
                draggable={true}
                onDragStart={(e: React.DragEvent) => onDragStart(e, 'delay')}
                onClick={() => addNode('delay')}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all flex items-center gap-2 font-black uppercase tracking-tighter text-[10px] cursor-grab active:cursor-grabbing"
              >
                <Clock size={14} className="text-blue-400" />
                + Delay
              </div>
            </div>

            <div className="w-px h-10 bg-white/10 mx-2" />

            <div className="flex items-center gap-2">
              <button
                onClick={saveWorkflow}
                disabled={isSaving}
                className="px-6 py-2.5 bg-[var(--t-primary)] text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-[var(--t-primary-dim)] hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                <span>Save Automation</span>
              </button>
              <button
                onClick={saveAsBlueprintUI}
                disabled={isSaving}
                className="px-4 py-2.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-xl font-bold flex items-center gap-2 hover:bg-purple-500/20 transition-all disabled:opacity-50"
              >
                <Zap size={16} />
                <span>Save as Blueprint</span>
              </button>
            </div>
          </div>

          {/* Custom Registry Panel */}
          <div className="absolute top-[160px] left-6 z-[100] w-64 bg-black/60 backdrop-blur-xl p-6 rounded-[2rem] border border-white/10 shadow-2xl flex flex-col gap-4">
             <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-purple-400 flex items-center gap-2">
                  <Library size={12} /> Custom Registry
                </h4>
                <span className="text-[8px] font-black bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full border border-purple-500/20">
                  {customNodes.length}
                </span>
             </div>

              <button 
              onClick={() => setShowCreateCustomModal(true)}
              className="w-full mt-2 py-2 flex items-center justify-center gap-2 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 rounded-xl border border-purple-500/20 transition-all text-[10px] font-black uppercase tracking-widest mb-4"
            >
               <Plus size={12} /> Create custom node
             </button>


             <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {customNodes.map((node: any) => (
                  <div
                    key={node.id}
                    draggable={true}
                    onDragStart={(e: React.DragEvent) => {
                      e.dataTransfer.setData('application/reactflow', JSON.stringify(node.data));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    className="p-3 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/50 hover:bg-white/10 transition-all cursor-grab group text-left"
                  >
                     <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-white truncate max-w-[120px]">{node.data.label}</span>
                        <button 
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            deleteCustomNode(node.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-all"
                        >
                          <Trash2 size={10} />
                        </button>
                     </div>
                     <p className="text-[8px] text-white/40 uppercase tracking-widest leading-tight">
                       {node.data.type} â€¢ {node.data.actionType || node.data.triggerType || 'Logic'}
                     </p>
                  </div>
                ))}
                {customNodes.length === 0 && (
                  <div className="text-center py-8 px-4 rounded-xl border border-dashed border-white/10">
                     <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest">No saved nodes</p>
                     <p className="text-[8px] text-white/10 mt-1 uppercase tracking-widest">Save a node from its settings to see it here</p>
                  </div>
                )}
             </div>
          </div>


          {/* React Flow Canvas */}
          <div className="flex-1 relative bg-[var(--t-bg)] mt-4">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={onInit}
              onConnectStart={onConnectStart}
              onConnectEnd={onConnectEnd}
              onNodesDelete={onNodesDelete}
              onNodeClick={onNodeClick}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              className="bg-dot-pattern"
            >
              <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="var(--t-border)" />
              <Controls />
              {isAutomationRunning && (
                <Panel position="top-right" className="bg-black/60 backdrop-blur-xl p-4 rounded-2xl border border-white/10 m-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-ping absolute" />
                      <div className="w-3 h-3 bg-green-500 rounded-full relative" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest text-green-500">
                      Automation Engine Firing
                    </span>
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </div>
        </div>
      )}

      {/* Node Settings Modal */}
      {isSettingsOpen && selectedNode && (
        <NodeSettingsModal
          node={selectedNode}
          onClose={() => setIsSettingsOpen(false)}
          onUpdate={updateNodeData}
          onSaveAsCustom={saveAsCustomNode}
          onDelete={(id) => {
            setNodes((nds: Node[]) => nds.filter(n => n.id !== id));
            setEdges((eds: Edge[]) => eds.filter(e => e.source !== id && e.target !== id));
            setIsSettingsOpen(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            onClick={() => setDeleteConfirmation(null)}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <div
            className="relative bg-[var(--t-surface)] border border-[var(--t-border)] rounded-[2.5rem] p-10 max-w-sm w-full text-center space-y-6 shadow-2xl"
          >
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Trash2 className="text-red-500 w-10 h-10" />
            </div>
            <div>
              <h3 className="text-2xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">
                Delete <span className="text-red-500">Automation?</span>
              </h3>
              <p className="text-[var(--t-text-muted)] text-sm mt-2">
                This will permanently remove <b>{deleteConfirmation.name}</b> from your dashboard and the engine.
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="flex-1 py-4 px-6 bg-[var(--t-surface-hover)] text-[var(--t-text)] rounded-2xl font-bold hover:bg-[var(--t-border)] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={deleteWorkflow}
                className="flex-1 py-4 px-6 bg-red-500 text-white rounded-2xl font-bold shadow-lg shadow-red-500/20 hover:scale-105 active:scale-95 transition-all"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Node Modal */}
      <Modal
        isOpen={showCreateCustomModal}
        onClose={() => setShowCreateCustomModal(false)}
        title="Create Custom Node Registry"
        maxWidth="max-w-md"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Node Name</label>
            <input
              type="text"
              value={newCustomNode.label}
              onChange={(e) => setNewCustomNode({ ...newCustomNode, label: e.target.value })}
              placeholder="e.g. Strategic Follow-up"
              className="w-full bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--t-primary)] transition-all"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Category</label>
            <select
              value={newCustomNode.type}
              onChange={(e) => setNewCustomNode({ ...newCustomNode, type: e.target.value as any })}
              className="w-full bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--t-primary)] transition-all"
            >
              <option value="trigger">Trigger</option>
              <option value="action">Action</option>
              <option value="condition">Condition</option>
              <option value="ai">AI / Logic</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Description</label>
            <textarea
              value={newCustomNode.description}
              onChange={(e) => setNewCustomNode({ ...newCustomNode, description: e.target.value })}
              placeholder="What does this node do?"
              className="w-full bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--t-primary)] transition-all resize-none h-20"
            />
          </div>

          <button
            onClick={() => {
              if (!newCustomNode.label) return toast.error('Label required');
              const node = {
                type: 'automation',
                data: { ...newCustomNode }
              };
              addCustomNode(node);
              setShowCreateCustomModal(false);
              toast.success('Registry updated');
            }}
            className="w-full py-2.5 bg-[var(--t-primary)] text-white font-bold rounded-xl hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 mt-2"
          >
            Add to Registry
          </button>
        </div>
      </Modal>
    </div>
  );
}


function NodeSettingsModal({ node, onClose, onUpdate, onDelete, onSaveAsCustom }: { 
  node: Node, 
  onClose: () => void, 
  onUpdate: (id: string, data: any) => void, 
  onDelete: (id: string) => void,
  onSaveAsCustom: (node: Node) => void
}) {
  const data = node.data as AutomationNodeData;

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      if (mainEl) mainEl.style.overflow = '';
    };
  }, []);

  // Standard modal styles for better readability
  const inputCls = "w-full bg-black/40 border border-[var(--t-border)] rounded-lg px-3 py-2 text-sm focus:border-[var(--t-primary)] focus:ring-1 focus:ring-[var(--t-primary)]/20 outline-none text-[var(--t-text)] transition-all";
  const labelCls = "block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] mb-1 ml-1";

  return createPortal(
    <div 
      onWheel={(e) => e.stopPropagation()} 
      onPointerDown={(e) => e.stopPropagation()}
      className="fixed inset-0 z-[9999]"
    >
      <motion.div
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'absolute', inset: 0 }}
        className="bg-black/60 backdrop-blur-sm z-[-1]"
      />
      <div 
        className="absolute" 
        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-[90vw] max-w-md max-h-[85vh] bg-[var(--t-surface)] border border-[var(--t-border)] shadow-2xl flex flex-col overflow-hidden rounded-2xl"
        >
        {/* Header */}
        <div className="p-4 border-b border-[var(--t-border)] flex items-center justify-between bg-[var(--t-surface-dim)]/50 shrink-0">
          <h2 className="text-sm font-black uppercase tracking-wider text-[var(--t-text)]">Node Configuration</h2>
          <button onClick={onClose} className="p-2 hover:bg-[var(--t-surface-hover)] rounded-md transition-all text-[var(--t-text-muted)] hover:text-[var(--t-text)]">
            <X size={16} />
          </button>
        </div>

        {/* Content Area - Scrollable if too long */}
        <div className="p-4 flex flex-col overflow-y-auto flex-1">
          {/* Form Fields Section */}
          <div className="flex flex-col gap-2">

          {/* Display Label — always shown */}
          <div className="space-y-0.5">
            <label className={labelCls}>Label</label>
            <input
              type="text"
              value={data.label || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { label: e.target.value })}
              className={inputCls}
            />
          </div>

          {/* TRIGGER fields */}
          {data.type === 'trigger' && (
            <div className="space-y-1">
              <div>
                <label className={labelCls}>Trigger Type</label>
                <select value={data.triggerType || ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { triggerType: e.target.value })} className={inputCls}>
                  <option value="new_lead">Lead Created</option>
                  <option value="status_change">Status Changed</option>
                  <option value="high_score">Score Reached</option>
                  <option value="task_completed">Task Completed</option>
                  <option value="sms_received">SMS Received</option>
                  <option value="email_received">Email Received</option>
                  <option value="webhook_received">Webhook Received</option>
                  <option value="schedule">Schedule / Time-based</option>
                  <option value="inactivity">Lead Inactivity</option>
                  <option value="lead_score_changed">Lead Score Changed</option>
                  <option value="lead_tag_added">Lead Tag Added/Removed</option>
                </select>
              </div>
              {data.triggerType === 'status_change' && (
                <div>
                  <label className={labelCls}>Triggering Status</label>
                  <select value={data.status || 'new'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { status: e.target.value })} className={inputCls}>
                    <option value="new">New Lead</option>
                    <option value="contacted">Contacted</option>
                    <option value="nurturing">Nurturing</option>
                    <option value="offer-made">Offer Made</option>
                    <option value="under-contract">Under Contract</option>
                    <option value="closed-won">Closed Won</option>
                    <option value="dead">Dead / Unqualified</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* CONDITION fields */}
          {data.type === 'condition' && (
            <div className="space-y-1">
              <div>
                <label className={labelCls}>Field</label>
                <select value={data.conditionField || 'status'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { conditionField: e.target.value })} className={inputCls}>
                  <option value="score_above">Score Above</option>
                  <option value="score_below">Score Below</option>
                  <option value="status">Status Equals</option>
                  <option value="location">City/State Equals</option>
                  <option value="tag_contains">Tag Contains</option>
                  <option value="time_elapsed">Time Since Contact</option>
                  <option value="equity_percent">Equity %</option>
                  <option value="source">Lead Source</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Operator</label>
                <select value={data.conditionOperator || 'equals'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { conditionOperator: e.target.value })} className={inputCls}>
                  <option value="equals">Equals</option>
                  <option value="not_equals">Does Not Equal</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                  <option value="contains">Contains</option>
                  <option value="not_contains">Does Not Contain</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Value</label>
                <input type="text" value={data.conditionValue || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { conditionValue: e.target.value })} className={inputCls} placeholder="e.g. closed-won, 80" />
              </div>
            </div>
          )}

          {/* DELAY fields */}
          {data.type === 'delay' && (
            <div className="space-y-1">
              <label className={labelCls}>Wait Duration</label>
              <div className="flex gap-1">
                <input type="number" value={data.duration || 1} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { duration: Number(e.target.value) })} className={inputCls} min="1" />
                <select value={data.delayUnit || 'hours'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { delayUnit: e.target.value })} className={inputCls}>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}

          {/* ACTION fields */}
          {data.type !== 'trigger' && data.type !== 'condition' && data.type !== 'delay' && data.type !== 'ai' && (
            <div className="space-y-1">
              <div>
                <label className={labelCls}>Action Type</label>
                <select value={data.actionType || ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { actionType: e.target.value })} className={inputCls}>
                  <option value="send_sms">Send SMS</option>
                  <option value="send_email">Send Email</option>
                  <option value="add_task">Create Task</option>
                  <option value="update_status">Update Status</option>
                  <option value="update_score">Update Lead Score</option>
                  <option value="create_note">Create Lead Note</option>
                  <option value="assign_lead">Assign Lead to User</option>
                  <option value="add_tag">Add/Remove Lead Tag</option>
                  <option value="call_webhook">Call Webhook</option>
                  <option value="notify">In-App Notify</option>
                  <option value="delay">Wait / Delay</option>
                  <option value="send_slack">Send Slack message</option>
                </select>
              </div>
              {(data.actionType === 'send_sms' || data.actionType === 'send_email' || data.actionType === 'notify') && (
                <div className="space-y-1">
                  {data.actionType === 'send_email' && (
                    <div>
                      <label className={labelCls}>Subject</label>
                      <input type="text" value={data.subject || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { subject: e.target.value })} className={inputCls} placeholder="Email Subject" />
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>Message</label>
                    <textarea value={data.message || ''} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdate(node.id, { message: e.target.value })} rows={2} className={`${inputCls} resize-none`} placeholder="Use {{name}} for tags..." />
                  </div>
                </div>
              )}
              {data.actionType === 'add_task' && (
                <div className="space-y-1">
                  <div>
                    <label className={labelCls}>Task Title</label>
                    <input type="text" value={data.taskTitle || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { taskTitle: e.target.value })} className={inputCls} placeholder="Follow up with {{name}}" />
                  </div>
                  <div>
                    <label className={labelCls}>Priority</label>
                    <select value={data.priority || 'high'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { priority: e.target.value })} className={inputCls}>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              )}
              {data.actionType === 'update_status' && (
                <div>
                  <label className={labelCls}>New Status</label>
                  <select value={data.status || 'new'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { status: e.target.value })} className={inputCls}>
                    <option value="new">New Lead</option>
                    <option value="contacted">Contacted</option>
                    <option value="nurturing">Nurturing</option>
                    <option value="offer-made">Offer Made</option>
                    <option value="under-contract">Under Contract</option>
                    <option value="closed-won">Closed Won</option>
                    <option value="dead">Dead / Unqualified</option>
                  </select>
                </div>
              )}
              {data.actionType === 'call_webhook' && (
                <div>
                  <label className={labelCls}>Webhook URL</label>
                  <input type="text" value={data.webhookUrl || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { webhookUrl: e.target.value })} className={inputCls} placeholder="https://api.example.com/webhook" />
                </div>
              )}
              {data.actionType === 'send_slack' && (
                <div>
                  <label className={labelCls}>Slack Channel</label>
                  <input type="text" value={data.slackChannel || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { slackChannel: e.target.value })} className={inputCls} placeholder="#sales-team" />
                </div>
              )}
              {data.actionType === 'assign_lead' && (
                <div>
                  <label className={labelCls}>Assign To (ID/Name)</label>
                  <input type="text" value={data.assignedTo || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { assignedTo: e.target.value })} className={inputCls} placeholder="User ID or Name" />
                </div>
              )}
              {data.actionType === 'add_tag' && (
                <div className="space-y-1">
                  <div>
                    <label className={labelCls}>Tag Name</label>
                    <input type="text" value={data.tagName || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { tagName: e.target.value })} className={inputCls} placeholder="e.g. Hot Lead" />
                  </div>
                  <div>
                    <label className={labelCls}>Action</label>
                    <select value={(data.tagAction as string) || 'add'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { tagAction: e.target.value })} className={inputCls}>
                      <option value="add">Add Tag</option>
                      <option value="remove">Remove Tag</option>
                    </select>
                  </div>
                </div>
              )}
              {data.actionType === 'update_score' && (
                <div>
                  <label className={labelCls}>New Score Value</label>
                  <input type="number" value={(data.scoreValue as number) || 0} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(node.id, { scoreValue: Number(e.target.value) })} className={inputCls} placeholder="e.g. 100" />
                </div>
              )}
              {data.actionType === 'create_note' && (
                <div>
                  <label className={labelCls}>Note Content</label>
                  <textarea value={(data.noteText as string) || ''} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdate(node.id, { noteText: e.target.value })} rows={2} className={`${inputCls} resize-none`} placeholder="Note text..." />
                </div>
              )}
            </div>
          )}

          {/* AI fields */}
          {data.type === 'ai' && (
            <div className="space-y-1">
              <div>
                <label className={labelCls}>AI Model</label>
                <select value={data.model || 'gemini-2.0-flash-lite'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate(node.id, { model: e.target.value })} className={inputCls}>
                  <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                  <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                  <option value="deepseek-v3">DeepSeek V3</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Prompt</label>
                <textarea value={data.prompt || ''} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdate(node.id, { prompt: e.target.value })} rows={3} className={`${inputCls} resize-none`} placeholder="Tell the AI what to do..." />
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Buttons Section - Fixed at bottom */}
        <div className="p-4 border-t border-[var(--t-border)] bg-[var(--t-surface-dim)]/30 shrink-0 flex flex-col gap-1.5">
            <div className="flex gap-2">
              <button
                onClick={() => onSaveAsCustom(node)}
                className="flex-1 px-2 py-2 bg-[var(--t-surface-hover)] text-[var(--t-text)] rounded-lg font-bold border border-[var(--t-border)] hover:border-[var(--t-primary)]/40 transition-all flex items-center justify-center gap-1.5 text-[9px] uppercase tracking-wider"
              >
                <Save size={10} /> Registry
              </button>
              <button
                onClick={() => onDelete(node.id)}
                className="flex-1 px-2 py-2 bg-red-500/5 text-red-500 rounded-lg font-bold border border-red-500/10 hover:bg-red-500/10 transition-all flex items-center justify-center gap-1.5 text-[9px] uppercase tracking-wider"
              >
                <Trash2 size={10} /> Delete
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest rounded-lg hover:brightness-110 active:scale-95 transition-all shadow-md text-[9px]"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </div>,
    document.body
  );
}


export default function AutomationsHub() {
  return (
    <ReactFlowProvider>
      <AutomationsHubContent />
    </ReactFlowProvider>
  );
}

